import type { SiteHealthCheck, SiteHealthFindingRow, SiteHealthSeverity } from "@/types/tina";

import { brokenDays, isNewToday } from "./describe-finding";

export interface SiteHealthGroup {
  key: string;
  platform: string | null;
  checkName: SiteHealthCheck;
  severity: SiteHealthSeverity;
  findings: SiteHealthFindingRow[];
}

/** One card per (platform, check) with 2+ findings; every other finding is its own card. */
export function groupFindings(rows: SiteHealthFindingRow[]): SiteHealthGroup[] {
  const byKey = new Map<string, SiteHealthFindingRow[]>();
  for (const r of rows) {
    if (!r.platform) continue;
    const key = `${r.platform}::${r.check_name}`;
    byKey.set(key, [...(byKey.get(key) ?? []), r]);
  }
  const grouped = new Set<string>();
  const groups: SiteHealthGroup[] = [];
  for (const [key, findings] of byKey) {
    if (findings.length < 2) continue;
    findings.forEach((f) => grouped.add(f.id));
    groups.push({
      key,
      platform: findings[0].platform,
      checkName: findings[0].check_name,
      severity: findings.some((f) => f.severity === "red") ? "red" : "amber",
      findings,
    });
  }
  for (const r of rows) {
    if (grouped.has(r.id)) continue;
    groups.push({ key: r.id, platform: r.platform, checkName: r.check_name, severity: r.severity, findings: [r] });
  }
  return groups;
}

/** Red first, then any new today, then the longest broken (tina spec §8 order). */
export function sortGroups(groups: SiteHealthGroup[], now: Date): SiteHealthGroup[] {
  const score = (g: SiteHealthGroup) => ({
    red: g.severity === "red" ? 0 : 1,
    fresh: g.findings.some((f) => isNewToday(f, now)) ? 0 : 1,
    days: -Math.max(...g.findings.map((f) => brokenDays(f, now))),
  });
  return [...groups].sort((a, b) => {
    const x = score(a);
    const y = score(b);
    return x.red - y.red || x.fresh - y.fresh || x.days - y.days || a.key.localeCompare(b.key);
  });
}
